import * as THREE from 'three'

/**
 * Maximum lightness shift factor. At colorVariation=1 and a hash of ±1,
 * the lightness shifts by ±MAX_LIGHTNESS_SHIFT on the HSL scale (0-1).
 * 0.3 keeps variation visible but prevents blow-out to white or crush to black.
 */
const MAX_LIGHTNESS_SHIFT = 0.5

/**
 * Deterministic hash from a string, returning a value in [-1, 1].
 * Uses djb2 accumulation + integer bit-mixing finalizer to ensure
 * strings that differ by only a few characters (e.g. "Cube010_45"
 * vs "Cube010_46") produce widely separated values.
 */
export function deterministicHash(str: string): number {
  let hash = 5381
  for (let i = 0; i < str.length; i++) {
    hash = ((hash << 5) + hash + str.charCodeAt(i)) | 0
  }
  // Bit-mixing finalizer (murmurhash3-style) for better distribution
  hash = (((hash >> 16) ^ hash) * 0x45d9f3b) | 0
  hash = (((hash >> 16) ^ hash) * 0x45d9f3b) | 0
  hash = (hash >> 16) ^ hash
  // Normalize to [-1, 1] using lower 16 bits
  return (hash & 0xffff) / 0x8000 - 1
}

/**
 * Compute a lightness-varied color from a base hex color.
 * Shifts lightness only (no hue or saturation change).
 *
 * @param baseHex - Base color as hex string (e.g., "#808080")
 * @param meshName - Mesh name used as deterministic seed
 * @param variation - Variation amount in [0, 1] range
 * @returns THREE.Color with lightness offset applied
 */
export function computeVariedColor(
  baseHex: string,
  meshName: string,
  variation: number
): THREE.Color {
  const color = new THREE.Color(baseHex)
  if (variation <= 0) return color

  const offset = deterministicHash(meshName) * variation * MAX_LIGHTNESS_SHIFT
  color.offsetHSL(0, 0, offset)
  return color
}
